import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { postJson } from '../lib/api.ts';
import { saveSession } from '../lib/session.ts';

const formatCpf = (value: string) => {
  const digits = value.replace(/\D/g, '').slice(0, 11);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)}.${digits.slice(3)}`;
  if (digits.length <= 9) return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6)}`;
  return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`;
};

const isValidCpf = (value: string) => {
  const digits = value.replace(/\D/g, '');
  if (digits.length !== 11) return false;
  if (/^(\d)\1+$/.test(digits)) return false;

  const calcDigit = (cpfDigits: string, length: number) => {
    const numbers = cpfDigits.slice(0, length).split('').map(Number);
    const factor = length + 1;
    const total = numbers.reduce((sum, num, index) => sum + num * (factor - index), 0);
    const remainder = total % 11;
    return remainder < 2 ? 0 : 11 - remainder;
  };

  const firstCheck = calcDigit(digits, 9);
  const secondCheck = calcDigit(digits, 10);
  return firstCheck === Number(digits[9]) && secondCheck === Number(digits[10]);
};

const IdentificationPage = () => {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [cpf, setCpf] = useState('');
  const [phone, setPhone] = useState('');
  const [error, setError] = useState('');
  const cpfRef = useRef<HTMLInputElement | null>(null);
  const phoneRef = useRef<any>(null);

  const handleSubmit = async (event: React.SubmitEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!cpf.trim() || !isValidCpf(cpf)) {
      setError('Por favor, preencha o CPF corretamente.');
      return;
    }

    if (!phone.trim() || !isValidPhoneNumber(phone)) {
      setError('Por favor, preencha o telefone corretamente.');
      return;
    }
    let customer: any
    try {
      const document = cpf.replace(/\D/g, '');
      customer = await postJson('/api/auth/identify', { document, name, phone });
      await saveSession({ customerId: customer.customer_id, name, cpf, phone, otp: '' });
    } catch (error) {
      setError((error as Error).message);
      return;
    }

    try {
      await postJson(`/api/auth/${customer.customer_id}/send-otp/${phone.replace(/\D/g, '')}`, {});
      navigate('/verification');
    } catch (error) {
      setError((error as Error).message);
    }
  };

  return (
    <main className="space-y-8">
      <section className="rounded-3xl bg-white p-8 shadow-md shadow-slate-200">
        <p className="text-sm uppercase tracking-[0.3em] text-red-500">Bem-vindo</p>
        <h1 className="mt-4 text-3xl font-semibold text-red-500">Identifique-se para começar</h1>
        <p className="mt-3 text-slate-500">Digite seus dados para receber o código no WhatsApp e acessar a negociação.</p>
      </section>

      <form onSubmit={handleSubmit} className="grid gap-4 rounded-3xl bg-red-50 p-8 shadow-inner shadow-slate-100">
        {error && <div className="rounded-2xl bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}

        <label className="space-y-2">
          <span className="text-sm text-slate-700">Nome completo</span>
          <input
            value={name}
            onChange={(event) => setName(event.target.value)}
            className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-slate-600 shadow-sm outline-none transition focus:border-slate-400"
            placeholder="Seu nome"
          />
        </label>

        <label className="space-y-2">
          <span className="text-sm text-slate-700">CPF</span>
          <input
            ref={cpfRef}
            value={cpf}
            onChange={(event) => setCpf(formatCpf(event.target.value))}
            onKeyDown={(event) => {
              if (event.key === 'Tab' && !event.shiftKey) {
                event.preventDefault();
                phoneRef.current?.focus();
              }
            }}
            className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-slate-600 shadow-sm outline-none transition focus:border-slate-400"
            placeholder="000.000.000-00"
          />
        </label>

        <label className="space-y-2">
          <span className="text-sm text-slate-700">Telefone</span>
          <PhoneInput
            international
            defaultCountry="BR"
            value={phone}
            onChange={(value) => setPhone(value ?? '')}
            ref={phoneRef}
            className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-slate-600 shadow-sm transition focus-within:border-slate-400"
            numberInputProps={{
              className: 'w-full bg-transparent text-slate-600 placeholder:text-slate-400 outline-none',
              style: { boxSizing: 'border-box' }
            }}
            placeholder="Digite seu telefone"
          />
        </label>

        <button
          type="submit"
          className="mt-4 inline-flex w-full justify-center rounded-2xl bg-red-600 px-5 py-4 text-sm font-semibold text-white shadow-lg shadow-slate-200 transition hover:bg-red-700"
        >
          Receber código no WhatsApp
        </button>
      </form>
    </main>
  );
};

export default IdentificationPage;
